import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {OutputMaterialSockets} from "#material/nodes/base/output/definition"
import {outputMaterialDefinition} from "#material/nodes/base/output/definition"
import type {CyclesReturns} from "#material/sockets/cycles"
import {ThreeShadowCatcher} from "#material/three/three-shadow-catcher"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import type {ThreeReturns} from "#material/sockets/three"
import {materialConnection} from "#material/nodes/base/connection/shorthand"
import {UvMap} from "#material/nodes/input/uv-map/uv-map"
import {BlendNormals} from "#material/nodes/vector/blend-normals/blend-normals"
import {ThreeColormassMaterial} from "#material/three/three-colormass-material"
import {BsdfPrincipled} from "#material/nodes/shader/bsdf-principled/bsdf-principled"
import {MaterialConnection} from "#material/nodes/base/connection/material-connection"
import type {InputMaterialConnection} from "#material/sockets/definition"
import {Three as THREE, ThreeTsl as TSL} from "#material/three/exports"
import {ScannedTransmission} from "#material/nodes/shader/scanned-transmission/scanned-transmission"
import {ProvideThreeTexture} from "#material/nodes/texture/provide-three-texture/provide-three-texture"
import {NormalMap} from "#material/nodes/vector/normal-map/normal-map"
import {Displacement} from "#material/nodes/vector/displacement/displacement"
import {TexImage} from "#material/nodes/texture/tex-image/tex-image"
import {HeightToNormal} from "#material/nodes/converter/height-to-normal/height-to-normal"
import {VectorMath} from "#material/nodes/converter/vector-math/vector-math"
import type {MaterialOutputNode} from "#material/cycles/types"
import {cyclesVec3} from "#material/cycles/helpers"
import {MixShader} from "#material/nodes/shader/mix-shader/mix-shader"
import {AddShader} from "#material/nodes/shader/add-shader/add-shader"
import {resolveTextureResolution} from "#material/nodes/texture/tex-image/helpers"

@registerMaterialGraphNode
export class OutputMaterial extends MaterialNode<OutputMaterialSockets> {
    public override get definition() {
        return outputMaterialDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<OutputMaterialSockets>> => {
        const {meshRenderSettings} = evaluator.context

        if (this.parameters.settings.shadowCatcher) {
            const shadowCatcher = new ThreeShadowCatcher()

            return {surface: shadowCatcher}
        }

        if (!meshRenderSettings?.displacementImage) {
            const surface = await evaluator.get(this.parameters.inputs.surface)
            if (!surface) return {surface: new ThreeColormassMaterial()}
            if (!(surface instanceof THREE.Material)) throw new Error("OutputMaterial: surface output must be a THREE.Material in ThreeJS mode")
            return {
                surface,
            }
        }

        const clonedGraph = this.clone({cloneSubNode: () => true})

        const {surface} = clonedGraph.parameters.inputs

        const bsdf = (() => {
            if (surface === undefined)
                return new BsdfPrincipled({
                    parameters: {
                        inputs: {},
                        settings: {},
                    },
                })

            const resolveBsdfPrincipled = (node: unknown) => {
                if (node instanceof MaterialConnection) {
                    return resolveBsdfPrincipled(node.parameters.value)
                } else if (node instanceof BsdfPrincipled) {
                    return node
                } else if (node instanceof ScannedTransmission) {
                    return resolveBsdfPrincipled(node.parameters.inputs.bsdf)
                } else if (node instanceof MixShader || node instanceof AddShader) {
                    try {
                        return resolveBsdfPrincipled(node.parameters.inputs.shader)
                    } catch (e) {
                        return resolveBsdfPrincipled(node.parameters.inputs.shader_001)
                    }
                } else {
                    throw new Error("ApplyMeshRenderSettings: applied surface material must resolve to BsdfPrincipled")
                }
            }

            return resolveBsdfPrincipled(surface)
        })()

        const {normal} = bsdf.parameters.inputs
        const {
            displacementImage,
            displacementUvChannel,
            displacementMin,
            displacementMax,
            displacementNormalStrength,
            displacementNormalSmoothness,
            displacementNormalOriginalResolution,
        } = meshRenderSettings

        //TODO: this doesn't account for primary and secondary UVs having different tangent spaces

        const getDisplacementTexture = async (): Promise<InputMaterialConnection<"color">> => {
            const uv = materialConnection(
                new UvMap({
                    parameters: {
                        inputs: {},
                        settings: {
                            uvMapIndex: displacementUvChannel,
                        },
                    },
                }),
                "uv",
            )

            const displacementImageTest = displacementImage.imageNode.generator({
                uv,
                extension: "REPEAT",
                interpolation: "Closest",
                projection: "FLAT",
                disableProgressiveLoading: true,
                forceOriginalResolution: displacementNormalOriginalResolution,
            }).color

            //Case 1: Displacement is provided as a texture, we do not need to bake it
            if (displacementImageTest instanceof MaterialConnection && displacementImageTest.parameters.key === "color") {
                if (displacementImageTest.parameters.value instanceof TexImage) {
                    return displacementImageTest
                }
            }

            //Case 2: Displacement is provided as a node graph, we need to bake it
            const convertedDisplacementImage = displacementImage.imageNode.generator({
                uv: materialConnection(
                    new UvMap({
                        parameters: {
                            inputs: {},
                            settings: {
                                uvMapIndex: 0,
                            },
                        },
                    }),
                    "uv",
                ),
                extension: "REPEAT",
                interpolation: "Closest",
                projection: "FLAT",
                disableProgressiveLoading: true,
                forceOriginalResolution: displacementNormalOriginalResolution,
            }).color

            const {threeRenderer, threeDefaultFloatTextureType} = evaluator.context
            if (!threeRenderer || !threeDefaultFloatTextureType)
                throw Error("ApplyMeshRenderSettings: threeRenderer and threeDefaultFloatTextureType must be defined in context")

            const bakedMaterial = new THREE.MeshBasicNodeMaterial()
            bakedMaterial.colorNode = (await evaluator.get(convertedDisplacementImage)).toColor()

            const getResolution = (originalResolution: number | undefined) => {
                if (displacementNormalOriginalResolution || evaluator.context.textureResolution === "original") return originalResolution
                else return resolveTextureResolution(evaluator.context.textureResolution)
            }

            const width = getResolution(displacementImage.metadata?.width ?? undefined) ?? 2048
            const height = getResolution(displacementImage.metadata?.height ?? undefined) ?? 2048

            const getFilter = () => {
                if (evaluator.context.forceFiltering === "nearest") return THREE.NearestFilter
                else return THREE.LinearFilter
            }

            const renderTarget = new THREE.RenderTarget(width, height, {
                type: threeDefaultFloatTextureType,
                minFilter: getFilter(),
                magFilter: getFilter(),
                format: THREE.RedFormat,
                colorSpace: THREE.NoColorSpace,
            })

            evaluator.context.onThreeCreatedTexture?.(renderTarget)

            const renderScene = new THREE.Scene()

            const planeMesh = new THREE.Mesh(new THREE.PlaneGeometry(1.0, 1.0), bakedMaterial)
            renderScene.add(planeMesh)

            const renderCamera = new THREE.OrthographicCamera(-0.5, 0.5, 0.5, -0.5, 0.1, 2)
            renderCamera.position.z = 1

            const rendererState = THREE.RendererUtils.saveRendererState(threeRenderer)

            threeRenderer.setRenderTarget(renderTarget)
            threeRenderer.clear()
            threeRenderer.render(renderScene, renderCamera)

            THREE.RendererUtils.restoreRendererState(threeRenderer, rendererState)

            planeMesh.geometry.dispose()
            planeMesh.material.dispose()

            const textureNode = TSL.texture(renderTarget.texture, await evaluator.get(uv))

            return materialConnection(
                new ProvideThreeTexture({
                    parameters: {
                        inputs: {},
                        settings: {color: textureNode},
                    },
                }),
                "color",
            )
        }

        const color = materialConnection(
            new HeightToNormal({
                parameters: {
                    inputs: {
                        color: await getDisplacementTexture(),
                    },
                    settings: {
                        displacementMax,
                        displacementMin,
                        displacementNormalStrength,
                        displacementNormalSmoothness,
                    },
                },
            }),
            "color",
        )

        if (!normal) {
            bsdf.parameters = {
                inputs: {
                    ...bsdf.parameters.inputs,
                    normal: materialConnection(
                        new NormalMap({
                            parameters: {
                                inputs: {
                                    color,
                                },
                                settings: {strength: 1},
                            },
                        }),
                        "normal",
                    ),
                },
                settings: {
                    ...bsdf.parameters.settings,
                },
            }
        } else {
            if (normal.parameters.key !== "normal") throw new Error("ApplyMeshRenderSettings: normal input must be linked to a normal material node output")
            const {value: node} = normal.parameters

            const patchNormalMap = (normalMap: NormalMap) => {
                const {color: originalColor} = normalMap.parameters.inputs

                if (!originalColor) {
                    normalMap.updateParameters({
                        inputs: {
                            ...normalMap.parameters.inputs,
                            color,
                        },
                        settings: {},
                    })
                } else {
                    normalMap.updateParameters({
                        inputs: {
                            ...normalMap.parameters.inputs,
                            color: materialConnection(
                                new BlendNormals({
                                    parameters: {
                                        inputs: {
                                            color1: originalColor,
                                            color2: color,
                                        },
                                        settings: {},
                                    },
                                }),
                                "color",
                            ),
                        },
                        settings: {},
                    })
                }
            }

            if (node instanceof NormalMap) patchNormalMap(node)
            else throw new Error("ApplyMeshRenderSettings: applied normal material must be a NormalMap")
        }

        return {
            surface: await evaluator.get(materialConnection(bsdf, "bsdf")),
        }
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<OutputMaterialSockets>> => {
        const materialOutputNode: MaterialOutputNode = await (async () => {
            const {meshRenderSettings} = evaluator.context

            const {displacement} = this.parameters.inputs

            if (!meshRenderSettings?.displacementImage)
                return {
                    type: "material_output",
                    inputs: await evaluator.getAll({
                        surface: this.parameters.inputs.surface,
                        volume: this.parameters.inputs.volume,
                        displacement: this.parameters.settings.displacementEnabled === false ? undefined : displacement,
                    }),
                    parameters: {
                        name: this.parameters.settings.name,
                        displacement: cyclesVec3(this.parameters.settings.displacement),
                        use_mis: undefined,
                        shadow_catcher: undefined,
                    },
                    resources: {},
                }

            const {displacementImage, displacementUvChannel, displacementNormalOriginalResolution} = meshRenderSettings

            const uv = materialConnection(
                new UvMap({
                    parameters: {
                        inputs: {},
                        settings: {
                            uvMapIndex: displacementUvChannel,
                        },
                    },
                }),
                "uv",
            )

            const displacementMin = meshRenderSettings.displacementMin ?? -1
            const displacementMax = meshRenderSettings.displacementMax ?? 1

            const displacementNode = materialConnection(
                new Displacement({
                    parameters: {
                        inputs: {
                            height: displacementImage.imageNode.generator({
                                uv,
                                extension: "REPEAT",
                                interpolation: "Linear",
                                projection: "FLAT",
                                forceOriginalResolution: displacementNormalOriginalResolution,
                            }).color,
                        },
                        settings: {
                            midlevel: -displacementMin / (displacementMax - displacementMin),
                            scale: displacementMax - displacementMin,
                        },
                    },
                }),
                "displacement",
            )

            if (!displacement || this.parameters.settings.displacementEnabled === false)
                return {
                    type: "material_output",
                    inputs: await evaluator.getAll({
                        surface: this.parameters.inputs.surface,
                        volume: this.parameters.inputs.volume,
                        displacement: displacementNode,
                    }),
                    parameters: {
                        name: this.parameters.settings.name,
                        displacement: cyclesVec3(this.parameters.settings.displacement),
                        use_mis: undefined,
                        shadow_catcher: undefined,
                    },
                    resources: {},
                }
            else
                return {
                    type: "material_output",
                    inputs: await evaluator.getAll({
                        surface: this.parameters.inputs.surface,
                        volume: this.parameters.inputs.volume,
                        displacement: materialConnection(
                            new VectorMath({
                                parameters: {
                                    inputs: {
                                        vector: displacement,
                                        vector_001: displacementNode,
                                    },
                                    settings: {
                                        operation: "ADD",
                                    },
                                },
                            }),
                            "vector",
                        ),
                    }),
                    parameters: {
                        name: this.parameters.settings.name,
                        displacement: cyclesVec3(this.parameters.settings.displacement),
                        use_mis: undefined,
                        shadow_catcher: undefined,
                    },
                    resources: {},
                }
        })()

        return {surface: [materialOutputNode, "surface"]}
    }
}
